import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  musicApi,
  type SpotifyImportDetail,
  type SpotifyImportJob,
  type SpotifyImportTrack,
} from "@/api/music";
import { Button } from "@/components/ui/Button";
import { Field, TextArea } from "@/components/ui/Field";
import { PageHeader } from "@/components/ui/PageHeader";
import { useToast } from "@/components/ui/Toast";
import { cn } from "@/lib/cn";

/**
 * Music → Playlist import. The user opens a Spotify playlist page,
 * pastes our JS snippet into devtools, then pastes the resulting
 * JSON into the textarea here. arr-in-one:
 *   1. Persists the job + per-track rows.
 *   2. Background-resolves each (artist, album) via MusicBrainz,
 *      upserts artist + album rows, fires auto-grabs.
 *   3. Once tracks land + Plex scans, the user clicks "Create Plex
 *      playlist" per job; we look every track up in Plex and POST
 *      /playlists with the matched rating-keys.
 *
 * Status polling: while any job is in `pending` or `resolving`, we
 * refetch every 4s. Once everything's settled the interval drops to
 * 30s.
 */
export function MusicPlaylistImport() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [pasted, setPasted] = useState("");
  const [expandedJob, setExpandedJob] = useState<number | null>(null);

  const jobs = useQuery({
    queryKey: ["music", "spotify-import", "list"],
    queryFn: musicApi.spotifyImport.list,
    refetchInterval: (q) => {
      const inFlight = (q.state.data ?? []).some(
        (j) => j.status === "pending" || j.status === "resolving",
      );
      return inFlight ? 4000 : 30000;
    },
  });

  const createMut = useMutation({
    mutationFn: musicApi.spotifyImport.create,
    onSuccess: (res) => {
      toast(`Import #${res.id} queued`, "success");
      setPasted("");
      setExpandedJob(res.id);
      qc.invalidateQueries({ queryKey: ["music", "spotify-import"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Import failed", "error"),
  });

  const submit = () => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(pasted);
    } catch (e) {
      toast(
        e instanceof Error
          ? `Couldn't parse JSON: ${e.message}`
          : "Paste the JSON the snippet copied to your clipboard",
        "error",
      );
      return;
    }
    if (!isImportShape(parsed)) {
      toast(
        "JSON doesn't have the expected shape — should be { playlist_name, tracks: [...] }",
        "error",
      );
      return;
    }
    createMut.mutate(parsed);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Music"
        title="Playlist import"
        description="Import a Spotify playlist into the library: each unique artist + album is MB-looked-up, added monitored, and auto-grabbed. Once tracks land and Plex scans, click Create Plex playlist per-job to materialise it."
      />

      <SnippetBlock />

      <PasteForm
        value={pasted}
        onChange={setPasted}
        onSubmit={submit}
        loading={createMut.isPending}
      />

      <section className="space-y-2">
        <h2 className="eyebrow">Recent imports</h2>
        {jobs.isLoading ? (
          <p className="text-sm text-foreground/55">Loading…</p>
        ) : (jobs.data ?? []).length === 0 ? (
          <div className="rounded border border-dashed border-line bg-surface/30 px-4 py-6 text-sm text-foreground/55">
            No imports yet. Paste a Spotify playlist above.
          </div>
        ) : (
          <ul className="space-y-2">
            {(jobs.data ?? []).map((job) => (
              <JobRow
                key={job.id}
                job={job}
                expanded={expandedJob === job.id}
                onToggle={() =>
                  setExpandedJob(expandedJob === job.id ? null : job.id)
                }
              />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/**
 * Copy-button block with the actual JS snippet the user pastes
 * into Spotify's devtools console. Sourced from a constant in
 * code so we render verbatim — no string-interp surprises.
 */
function SnippetBlock() {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(SPOTIFY_SCRAPER_SNIPPET);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      toast(
        e instanceof Error ? e.message : "Clipboard write failed",
        "error",
      );
    }
  };
  return (
    <section className="space-y-2 rounded border border-line bg-surface/30 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="eyebrow">Step 1 — paste into Spotify devtools</h2>
        <Button size="sm" variant="secondary" onClick={onCopy}>
          {copied ? "Copied" : "Copy snippet"}
        </Button>
      </div>
      <p className="text-xs leading-relaxed text-foreground/65">
        Open a Spotify playlist in Chrome, open devtools (⌥⌘I), switch
        to the Console tab, paste the snippet, hit enter. The snippet
        walks the DOM (Spotify lazy-loads — it scrolls through the
        list and stitches every visible row), then copies a JSON
        payload to your clipboard. Then paste the clipboard contents
        into the box below.
      </p>
      <pre className="overflow-x-auto rounded bg-background/60 p-3 font-mono text-[11px] leading-relaxed text-foreground/85">
        {SPOTIFY_SCRAPER_SNIPPET}
      </pre>
    </section>
  );
}

function PasteForm({
  value,
  onChange,
  onSubmit,
  loading,
}: {
  value: string;
  onChange: (s: string) => void;
  onSubmit: () => void;
  loading: boolean;
}) {
  return (
    <section className="space-y-2">
      <Field
        label="Step 2 — paste the JSON the snippet copied"
        hint="Should look like {playlist_name, tracks: [...]}. Submitting kicks off a background resolver that walks each (artist, album) via MusicBrainz."
      >
        <TextArea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder='{"playlist_name": "Discover Weekly", "tracks": [...]}'
          rows={6}
          className="font-mono text-[12px]"
        />
      </Field>
      <div className="flex justify-end">
        <Button onClick={onSubmit} loading={loading}>
          Import
        </Button>
      </div>
    </section>
  );
}

function JobRow({
  job,
  expanded,
  onToggle,
}: {
  job: SpotifyImportJob;
  expanded: boolean;
  onToggle: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const detail = useQuery({
    queryKey: ["music", "spotify-import", "detail", job.id],
    queryFn: () => musicApi.spotifyImport.get(job.id),
    enabled: expanded,
  });

  const plexMut = useMutation({
    mutationFn: () => musicApi.spotifyImport.createPlexPlaylist(job.id),
    onSuccess: (res) => {
      toast(
        `Plex playlist created — ${res.matched_tracks} matched, ${res.missing_tracks} missing`,
        "success",
      );
      qc.invalidateQueries({ queryKey: ["music", "spotify-import"] });
    },
    onError: (e) =>
      toast(
        e instanceof Error ? e.message : "Plex playlist creation failed",
        "error",
      ),
  });

  return (
    <li className="rounded border border-line bg-surface/30">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-start gap-3 px-3 py-2 text-left"
      >
        <span className="min-w-0 flex-1">
          <span className="font-medium">{job.playlist_name}</span>
          <span className="ml-2 font-mono text-[10px] uppercase text-foreground/40">
            #{job.id}
          </span>
          {job.source_spotify_id && (
            <a
              href={`https://open.spotify.com/playlist/${job.source_spotify_id}`}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="ml-2 text-[11px] text-accent hover:underline"
            >
              spotify ↗
            </a>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-foreground/65">
            <StatusBadge status={job.status} />
            <span className="font-mono">
              {new Date(job.created_at).toLocaleString()}
            </span>
            {job.error && <span className="text-bad">{job.error}</span>}
            {job.plex_playlist_rating_key && (
              <span className="text-good">
                plex#{job.plex_playlist_rating_key}
              </span>
            )}
          </div>
        </span>
        <span className="font-mono text-[10px] text-foreground/40">
          {expanded ? "▼" : "▶"}
        </span>
      </button>
      {expanded && (
        <div className="space-y-3 border-t border-line/60 px-3 py-2.5">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              loading={plexMut.isPending}
              disabled={
                plexMut.isPending ||
                job.status === "pending" ||
                job.status === "resolving"
              }
              onClick={() => plexMut.mutate()}
              title={
                job.status === "pending" || job.status === "resolving"
                  ? "Wait for the resolver to finish before creating the Plex playlist"
                  : "Look every track up in Plex and create the audio playlist with what's present"
              }
            >
              {job.plex_playlist_rating_key
                ? "Recreate Plex playlist"
                : "Create Plex playlist"}
            </Button>
          </div>
          {detail.isLoading ? (
            <p className="text-xs text-foreground/55">Loading tracks…</p>
          ) : detail.isError ? (
            <p className="text-xs text-bad">
              {detail.error instanceof Error
                ? detail.error.message
                : "Couldn't load track detail"}
            </p>
          ) : (
            <TrackTable detail={detail.data!} />
          )}
        </div>
      )}
    </li>
  );
}

function TrackTable({ detail }: { detail: SpotifyImportDetail }) {
  const resolved = detail.tracks.filter((t) => t.resolved_album_id != null);
  const failed = detail.tracks.filter(
    (t) => t.resolved_album_id == null && t.resolution_note,
  );
  const pending = detail.tracks.filter(
    (t) => t.resolved_album_id == null && !t.resolution_note,
  );
  const summary = useMemo(
    () =>
      `${resolved.length} resolved · ${failed.length} failed · ${pending.length} pending — ${detail.tracks.length} total`,
    [resolved.length, failed.length, pending.length, detail.tracks.length],
  );
  return (
    <div className="space-y-2">
      <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-foreground/45">
        {summary}
      </div>
      <ul className="space-y-1">
        {detail.tracks.map((t) => (
          <TrackRow key={t.id} t={t} />
        ))}
      </ul>
    </div>
  );
}

function TrackRow({ t }: { t: SpotifyImportTrack }) {
  const tone = t.resolved_album_id != null ? "good" : t.resolution_note ? "bad" : "neutral";
  const colour =
    tone === "good"
      ? "border-good/30 bg-good/5"
      : tone === "bad"
        ? "border-bad/30 bg-bad/5"
        : "border-line bg-surface/40";
  return (
    <li
      className={cn(
        "flex flex-col gap-x-3 gap-y-0.5 rounded border px-2.5 py-1.5 text-xs sm:flex-row sm:items-baseline",
        colour,
      )}
    >
      <span className="min-w-0 flex-1 truncate">
        <strong className="font-medium">{t.track_title}</strong>
        <span className="ml-2 text-foreground/60">
          · {t.artist_name} — {t.album_name}
        </span>
      </span>
      {t.resolved_album_id != null && (
        <Link
          to="/music/albums/$id"
          params={{ id: String(t.resolved_album_id) }}
          className="text-accent hover:underline"
        >
          album →
        </Link>
      )}
      {t.plex_rating_key && (
        <span className="font-mono text-[10px] text-good">
          plex#{t.plex_rating_key}
        </span>
      )}
      {t.resolution_note && (
        <span className="text-bad">{t.resolution_note}</span>
      )}
    </li>
  );
}

function StatusBadge({ status }: { status: string }) {
  const tone =
    status === "resolved"
      ? "text-good"
      : status === "playlist_created"
        ? "text-good"
        : status === "failed"
          ? "text-bad"
          : status === "resolving"
            ? "text-accent"
            : "text-foreground/55";
  return (
    <span
      className={cn(
        "rounded border border-line/60 bg-surface-2/50 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em]",
        tone,
      )}
    >
      {status}
    </span>
  );
}

/**
 * Loose runtime check that the pasted JSON looks like our expected
 * payload shape. Doesn't enforce types deeply — the backend
 * deserialiser does the strict version. Just keeps obviously-wrong
 * pastes from triggering a server round-trip.
 */
function isImportShape(x: unknown): x is {
  playlist_name: string;
  source_spotify_id?: string;
  tracks: { track: string; artist: string; album: string }[];
} {
  if (!x || typeof x !== "object") return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o.playlist_name === "string" &&
    Array.isArray(o.tracks) &&
    o.tracks.every(
      (t) =>
        t &&
        typeof t === "object" &&
        typeof (t as Record<string, unknown>).track === "string" &&
        typeof (t as Record<string, unknown>).artist === "string" &&
        typeof (t as Record<string, unknown>).album === "string",
    )
  );
}

/**
 * The actual JS snippet the user pastes in Spotify devtools.
 * Pure DOM scrape — no Spotify API auth (the public
 * `open.spotify.com/get_access_token` endpoint started returning
 * 403 in May 2026, so we walk the rendered tracklist instead).
 *
 * Approach:
 *   1. Grab the playlist title from `[data-testid="entityTitle"]`
 *      (h1 fallback when Spotify rotates testids).
 *   2. Iterate `[data-testid="tracklist-row"]` rows currently in
 *      the DOM. Each row contains an anchor with `href="/track/{id}"`
 *      (track name) and one or more `a[href^="/artist/"]` +
 *      `a[href^="/album/"]` anchors.
 *   3. Spotify virtualises the list — only rows in viewport
 *      exist. So we scroll incrementally (~70% of viewport per
 *      step), harvest after each pause, repeat until two
 *      consecutive scrolls yield no new tracks.
 *
 * The seen-map is keyed by Spotify track id, so re-harvesting
 * already-scraped rows is idempotent.
 *
 * Lives in code as a string constant so the rendered <pre> is the
 * exact text we ship — no markdown / templating surprises.
 */
const SPOTIFY_SCRAPER_SNIPPET = `(async () => {
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  const playlistId = location.pathname.match(/\\/playlist\\/([A-Za-z0-9]+)/)?.[1];
  if (!playlistId) { console.error('arr-in-one: not on a /playlist/ page'); return; }

  const titleEl = document.querySelector('[data-testid="entityTitle"]')
    || document.querySelector('h1');
  const playlistName = titleEl?.textContent?.trim() || 'Spotify Playlist';

  // Find the scroll container that actually CONTAINS the tracklist.
  // The naive "first candidate that accepts a scroll" approach picks
  // the left playlists sidebar — also scrollable, but the wrong
  // pane. Anchoring on an actual tracklist row and walking up its
  // ancestors finds the right pane unambiguously.
  const findScrollParent = (el) => {
    let p = el && el.parentElement;
    while (p && p !== document.body) {
      const s = getComputedStyle(p);
      if ((s.overflowY === 'auto' || s.overflowY === 'scroll')
          && p.scrollHeight > p.clientHeight) return p;
      p = p.parentElement;
    }
    return document.scrollingElement || document.documentElement;
  };
  const anchorRow = document.querySelector('[data-testid="tracklist-row"]');
  if (!anchorRow) {
    console.error('arr-in-one: no [data-testid="tracklist-row"] in the DOM — make sure the playlist is open in the right pane before running the snippet.');
    return;
  }
  const scroller = findScrollParent(anchorRow);
  console.log('arr-in-one: scrolling', scroller);

  const seen = new Map();
  const harvest = () => {
    for (const row of document.querySelectorAll('[data-testid="tracklist-row"]')) {
      const trackA = row.querySelector('a[href^="/track/"]');
      const trackId = trackA?.getAttribute('href')?.match(/\\/track\\/([A-Za-z0-9]+)/)?.[1];
      if (!trackId || seen.has(trackId)) continue;
      const trackName = trackA.textContent.trim();
      const artistA = row.querySelector('a[href^="/artist/"]');
      const artist = artistA?.textContent.trim() || '';
      const albumA = row.querySelector('a[href^="/album/"]');
      const albumId = albumA?.getAttribute('href')?.match(/\\/album\\/([A-Za-z0-9]+)/)?.[1];
      const albumCol = row.querySelector('[aria-colindex="3"]');
      const album = (albumA?.textContent.trim() || albumCol?.textContent.trim() || '').trim();
      seen.set(trackId, {
        track: trackName,
        artist,
        album,
        spotify_track_id: trackId,
        spotify_album_id: albumId || undefined,
      });
    }
  };

  // Start at the top so we don't miss rows above the user's
  // scroll position when they paste.
  scroller.scrollTop = 0;
  await sleep(400);
  harvest();
  console.log('arr-in-one: initial harvest = ' + seen.size + ' tracks');

  let stableRounds = 0;
  let lastSize = seen.size;
  let lastScroll = scroller.scrollTop;
  while (stableRounds < 3) {
    scroller.scrollTop = scroller.scrollTop + (scroller.clientHeight || 600) * 0.7;
    await sleep(450);
    harvest();
    const reachedBottom = scroller.scrollTop === lastScroll;
    if (seen.size === lastSize) { stableRounds++; } else { stableRounds = 0; }
    if (reachedBottom) { stableRounds = Math.max(stableRounds, 2); }
    lastSize = seen.size;
    lastScroll = scroller.scrollTop;
    if (seen.size > 5000) break; // safety cap
  }

  const tracks = Array.from(seen.values());
  if (tracks.length === 0) {
    console.error('arr-in-one: no tracks scraped. Selector [data-testid="tracklist-row"] returned nothing — Spotify may have changed its DOM.');
    return;
  }
  const payload = JSON.stringify({ playlist_name: playlistName, source_spotify_id: playlistId, tracks });

  // Clipboard write strategy:
  //   1. navigator.clipboard.writeText — works when the page is
  //      focused. Fails with NotAllowedError when devtools has
  //      focus (the common case for paste-and-run snippets).
  //   2. window.copy(...) — Chrome/Firefox devtools utility that
  //      copies its argument regardless of page focus. Always
  //      available inside the devtools console.
  //   3. Last resort: dump the JSON to console so the user can
  //      select + ⌘C manually.
  let copied = false;
  try {
    await navigator.clipboard.writeText(payload);
    copied = true;
  } catch (_) { /* fall through */ }
  if (!copied && typeof copy === 'function') {
    try { copy(payload); copied = true; } catch (_) { /* fall through */ }
  }
  if (copied) {
    console.log('arr-in-one: copied ' + tracks.length + ' tracks from "' + playlistName + '" to clipboard. Paste into arr-in-one.');
  } else {
    console.error('arr-in-one: clipboard write failed. Select + copy the JSON below manually:');
    console.log(payload);
  }
})();
`;
